// Shared clean-up for anything a person types (comments, messages, captions, bios, titles, reports...).
// It runs before the text is moderated and written to the database. The database layer is Firestore: documents are
// written through typed SDK calls, never by building query strings, so there is no SQL anywhere in the app and nothing
// to inject into. React escapes text when it is drawn, and the few server-built HTML pages escape their values.
// This file is the single place that normalises text so every entry point treats it the same way.

// Longest text each field may hold. They sit at or below the limits in firestore.rules, which are the real server-side cap.
export const TEXT_LIMITS = {
  comment: 1000,
  message: 2000,
  caption: 2200,
  trick: 40,
  bio: 300,
  name: 50,
  title: 80,
  description: 1000,
  place: 60,
  report: 1000,
  offer: 300,
  review: 200,
} as const;

// Control characters (other than tab / newline) and invisible or direction-flipping characters that are used to spoof text.
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g;
const INVISIBLE_CHARS = /[​-‏‪-‮⁠-⁤⁦-⁩﻿]/g;

export const cleanText = (value: unknown, max: number, options: { multiline?: boolean } = {}): string => {
  const { multiline = true } = options;
  let text = typeof value === 'string' ? value : value == null ? '' : String(value);
  text = text.normalize('NFC').replace(CONTROL_CHARS, '').replace(INVISIBLE_CHARS, '').replace(/\r\n?/g, '\n');
  text = multiline
    ? text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n')
    : text.replace(/\s+/g, ' ');
  text = text.trim();
  // Count whole characters (emoji are two UTF-16 units) so a cut never leaves half of one.
  const chars = Array.from(text);
  return chars.length > max ? chars.slice(0, max).join('').trimEnd() : text;
};

export const cleanLine = (value: unknown, max: number): string => cleanText(value, max, { multiline: false });

// A price is a finite, non-negative number with at most two decimals.
export const cleanPrice = (value: unknown, max = 1_000_000): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(Math.min(n, max) * 100) / 100;
};

export const isValidEmail = (value: string): boolean =>
  value.length <= 254 && /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[A-Za-z]{2,}$/.test(value);

// Only plain https links are allowed through (blocks javascript:, data:, file: and the like).
export const safeHttpsUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.length > 2048) return null;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
};
